import { useToast } from "@/hooks/use-toast";
import { useBrandColors } from "@/lib/brand-colors";
import { useState } from "react";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis } from "recharts";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ApifyCostAlertsResponse, ApifyUsageByUserDetailResponse, ApifyUsageByUserResponse, ApifyUsageResponse } from "./types";
import { formatDate, formatNum, formatUsd } from "./helpers";
import { ApifyAlertSettingsCard } from "./ApifyAlertSettingsCard";

export function ApifyUsagePanel() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const brandColors = useBrandColors();
  const [showAcknowledgedApify, setShowAcknowledgedApify] = useState<boolean>(false);
  const apifyAlertsQuery = useQuery<ApifyCostAlertsResponse>({
    queryKey: ["/api/admin/apify-cost-alerts", 25, showAcknowledgedApify],
    queryFn: async () => {
      const res = await fetch(
        `/api/admin/apify-cost-alerts?limit=25&includeAcknowledged=${showAcknowledgedApify ? "true" : "false"}`,
        { credentials: "include" },
      );
      if (!res.ok) throw new Error("Failed to load Apify cost alerts");
      return res.json();
    },
  });
  const apifyAcknowledgeMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async ({ id, acknowledge }: { id: number; acknowledge: boolean }) => {
      const path = acknowledge ? "acknowledge" : "unacknowledge";
      return apiRequest("POST", `/api/admin/apify-cost-alerts/${id}/${path}`);
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/apify-cost-alerts"] });
      toast({ title: vars.acknowledge ? "Alert acknowledged" : "Alert reopened" });
    },
    onError: (err: any) => {
      toast({
        title: "Action failed",
        description: parseErrorMessage(err, "Could not update alert."),
        variant: "destructive",
      });
    },
  });
  const dailyQuery = useQuery<{ days: number; daily: ApifyUsageResponse["daily"] }>({
    queryKey: ["/api/admin/apify-usage/daily", 7],
    queryFn: async () => {
      const res = await fetch(`/api/admin/apify-usage/daily?days=7`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load Apify daily usage");
      return res.json();
    },
  });
  const summaryQuery = useQuery<ApifyUsageResponse["summary"]>({
    queryKey: ["/api/admin/apify-usage/summary"],
    queryFn: async () => {
      const res = await fetch(`/api/admin/apify-usage/summary`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load Apify usage summary");
      return res.json();
    },
  });
  const recentQuery = useQuery<{ limit: number; recent: ApifyUsageResponse["recent"] }>({
    queryKey: ["/api/admin/apify-usage/recent", 10],
    queryFn: async () => {
      const res = await fetch(`/api/admin/apify-usage/recent?limit=10`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load recent Apify runs");
      return res.json();
    },
  });
  const [byUserDays, setByUserDays] = useState<number>(30);
  const byUserQuery = useQuery<ApifyUsageByUserResponse>({
    queryKey: ["/api/admin/apify-usage/by-user", byUserDays],
    queryFn: async () => {
      const res = await fetch(`/api/admin/apify-usage/by-user?days=${byUserDays}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load Apify usage by user");
      return res.json();
    },
  });

  // Task #3917 — drill-down: clicking a "Usage by user" row opens a dialog
  // listing that user's individual runs within the same window. `null` userId
  // is the system/unattributed bucket (queried with userId=unattributed).
  const [drillUser, setDrillUser] = useState<{ userId: number | null; label: string } | null>(null);
  const drillUserParam = drillUser == null ? null : drillUser.userId == null ? "unattributed" : String(drillUser.userId);
  const drillQuery = useQuery<ApifyUsageByUserDetailResponse>({
    queryKey: ["/api/admin/apify-usage/by-user/detail", drillUserParam, byUserDays],
    enabled: drillUser != null,
    queryFn: async () => {
      const res = await fetch(
        `/api/admin/apify-usage/by-user/detail?userId=${drillUserParam}&days=${byUserDays}&limit=100`,
        { credentials: "include" },
      );
      if (!res.ok) throw new Error("Failed to load Apify runs for user");
      return res.json();
    },
  });

  const isLoading = dailyQuery.isLoading || summaryQuery.isLoading || recentQuery.isLoading;
  const isError = dailyQuery.isError || summaryQuery.isError || recentQuery.isError;
  const refetchAll = () => {
    dailyQuery.refetch();
    summaryQuery.refetch();
    recentQuery.refetch();
  };

  const ACTOR_LABELS: Record<string, string> = {
    "maxcopell/zillow-detail-scraper": "Zillow — single import",
    "maxcopell/zillow-scraper": "Zillow — Buy Box city scan",
  };
  const ACTOR_COLORS = [brandColors.brand, brandColors.brandAccent, "#16a34a", "#a855f7", "#ef4444", "#0ea5e9", "#eab308", "#ec4899"];
  const OTHER_ACTOR_COLOR = "#94a3b8";
  const MAX_ACTOR_SERIES = 6;

  const { chartData, actorSeries } = (() => {
    const data = dailyQuery.data;
    if (!data) {
      return {
        chartData: [] as Array<Record<string, number | string>>,
        actorSeries: [] as Array<{ key: string; label: string; color: string }>,
      };
    }
    // Rank actors by total cost over the window so the busiest scrapers
    // get a dedicated bar; the long tail rolls up into "Other".
    const actorTotals = new Map<string, number>();
    for (const r of data.daily) {
      actorTotals.set(r.actor, (actorTotals.get(r.actor) ?? 0) + r.costUsd);
    }
    const ranked = Array.from(actorTotals.entries())
      .map(([actor, total]) => ({ actor, total }))
      .sort((a, b) => b.total - a.total);
    const topActors = ranked.slice(0, MAX_ACTOR_SERIES).map((r) => r.actor);
    const topSet = new Set(topActors);
    const hasOther = ranked.length > MAX_ACTOR_SERIES;
    const series: Array<{ key: string; label: string; color: string }> = topActors.map((actor, i) => ({
      key: actor,
      label: ACTOR_LABELS[actor] ?? actor,
      color: ACTOR_COLORS[i % ACTOR_COLORS.length],
    }));
    if (hasOther) {
      series.push({ key: "__other__", label: "Other actors", color: OTHER_ACTOR_COLOR });
    }

    const byDay = new Map<string, Record<string, number | string>>();
    // Seed all 7 days (including empty ones) so the chart bars are evenly spaced.
    for (let i = 6; i >= 0; i--) {
      const d = new Date(Date.now() - i * 24 * 60 * 60 * 1000);
      const key = d.toISOString().slice(0, 10);
      const row: Record<string, number | string> = { day: key.slice(5) };
      for (const s of series) row[s.key] = 0;
      byDay.set(key, row);
    }
    for (const r of data.daily) {
      const key = r.bucketStart.slice(0, 10);
      const row = byDay.get(key);
      if (!row) continue;
      const bucketKey = topSet.has(r.actor) ? r.actor : "__other__";
      if (row[bucketKey] == null) row[bucketKey] = 0;
      row[bucketKey] = (Number(row[bucketKey]) || 0) + r.costUsd;
    }
    return { chartData: Array.from(byDay.values()), actorSeries: series };
  })();

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold" data-testid="text-apify-usage-title">Apify Usage</h3>
            <p className="text-sm text-muted-foreground">
              Per-actor spend across every Apify actor PadPulse runs. The 7-day chart shows the top spenders with the long tail rolled into "Other actors." Cost &amp; compute-unit figures come directly from Apify's run metadata.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                try {
                  const res = await fetch(`/api/admin/apify-usage/backfill`, {
                    method: "POST",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ maxRuns: 1000 }),
                  });
                  const data = await res.json();
                  if (!res.ok) throw new Error(data?.message || "Backfill failed");
                  toast({
                    title: "Backfill complete",
                    description: `Scanned ${data.totalScanned}, inserted ${data.inserted}, skipped ${data.skipped} duplicates.`,
                  });
                  refetchAll();
                } catch (e: any) {
                  toast({ title: "Backfill failed", description: parseErrorMessage(e, String(e)), variant: "destructive" });
                }
              }}
              data-testid="button-backfill-apify-usage"
            >
              Backfill from Apify
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={refetchAll}
              data-testid="button-refresh-apify-usage"
            >
              Refresh
            </Button>
          </div>
        </div>
      </Card>

      {isLoading ? (
        <Card className="p-6 space-y-3">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
        </Card>
      ) : isError ? (
        <Card className="p-6 text-sm text-destructive" data-testid="text-apify-usage-error">
          Failed to load Apify usage. Please try again.
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="p-4">
              <div className="text-xs text-muted-foreground">30-day spend</div>
              <div className="text-2xl font-semibold mt-1" data-testid="text-apify-total-cost">
                ${(summaryQuery.data!.costUsd).toFixed(2)}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {summaryQuery.data!.runs.toLocaleString()} runs
              </div>
            </Card>
            <Card className="p-4">
              <div className="text-xs text-muted-foreground">Daily average</div>
              <div className="text-2xl font-semibold mt-1" data-testid="text-apify-daily-avg">
                ${(summaryQuery.data!.dailyAverageUsd).toFixed(2)}
              </div>
              <div className="text-xs text-muted-foreground mt-1">over last 30 days</div>
            </Card>
            <Card className="p-4">
              <div className="text-xs text-muted-foreground">Compute units (30d)</div>
              <div className="text-2xl font-semibold mt-1" data-testid="text-apify-compute-units">
                {summaryQuery.data!.computeUnits.toFixed(2)}
              </div>
            </Card>
            <Card className="p-4">
              <div className="text-xs text-muted-foreground">Run outcomes (30d)</div>
              <div className="text-2xl font-semibold mt-1" data-testid="text-apify-success-rate">
                {summaryQuery.data!.successes} ok
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {summaryQuery.data!.failures} failed
              </div>
            </Card>
          </div>

          <Card className="p-4">
            <div className="mb-3">
              <h4 className="text-sm font-semibold">Last 7 days — cost by actor</h4>
            </div>
            {actorSeries.length === 0 || chartData.every((d) => actorSeries.every((s) => (Number(d[s.key]) || 0) === 0)) ? (
              <div className="text-sm text-muted-foreground py-12 text-center" data-testid="text-apify-chart-empty">
                No Apify runs in the last 7 days.
              </div>
            ) : (
              <div style={{ width: "100%", height: 260 }} data-testid="chart-apify-daily">
                <ResponsiveContainer>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="day" />
                    <YAxis tickFormatter={(v) => `$${v}`} />
                    <RechartsTooltip formatter={(v: any) => `$${Number(v).toFixed(2)}`} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    {actorSeries.map((s) => (
                      <Bar key={s.key} dataKey={s.key} name={s.label} stackId="cost" fill={s.color} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>

          <Card className="p-4">
            <div className="mb-3">
              <h4 className="text-sm font-semibold">10 most recent runs</h4>
            </div>
            {recentQuery.data!.recent.length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center" data-testid="text-apify-recent-empty">
                No runs logged yet.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>When</TableHead>
                      <TableHead>Actor</TableHead>
                      <TableHead>Trigger</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Compute</TableHead>
                      <TableHead className="text-right">Cost</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recentQuery.data!.recent.map((r) => (
                      <TableRow key={r.id} data-testid={`row-apify-run-${r.id}`}>
                        <TableCell className="whitespace-nowrap text-xs">
                          {new Date(r.createdAt).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-xs font-mono">{r.actor}</TableCell>
                        <TableCell className="text-xs">
                          <div>{r.trigger}</div>
                          {r.triggerDetail && (
                            <div className="text-muted-foreground truncate max-w-[240px]">{r.triggerDetail}</div>
                          )}
                        </TableCell>
                        <TableCell className="text-xs">
                          {r.userEmail ?? r.userDisplayName ?? (r.userId == null ? "—" : `#${r.userId}`)}
                        </TableCell>
                        <TableCell className="text-xs">{r.status}</TableCell>
                        <TableCell className="text-xs text-right">
                          {r.computeUnits == null ? "—" : r.computeUnits.toFixed(3)}
                        </TableCell>
                        <TableCell className="text-xs text-right">
                          {r.costUsd == null ? "—" : `$${r.costUsd.toFixed(4)}`}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </Card>

          <Card className="p-4" data-testid="card-apify-usage-by-user">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h4 className="text-sm font-semibold">Usage by user</h4>
                <p className="text-xs text-muted-foreground">
                  Apify spend attributed to each PadPulse user over the selected window, sorted by total cost. Runs with no user (system/backfill) roll up into "System / unattributed".
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Label htmlFor="select-apify-by-user-window" className="text-xs text-muted-foreground">
                  Window
                </Label>
                <select
                  id="select-apify-by-user-window"
                  className="h-8 rounded-md border bg-background px-2 text-xs"
                  value={byUserDays}
                  onChange={(e) => setByUserDays(Number(e.target.value))}
                  data-testid="select-apify-by-user-window"
                >
                  <option value={7}>Last 7 days</option>
                  <option value={30}>Last 30 days</option>
                  <option value={90}>Last 90 days</option>
                </select>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => byUserQuery.refetch()}
                  data-testid="button-refresh-apify-by-user"
                >
                  Refresh
                </Button>
              </div>
            </div>
            {byUserQuery.isLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
              </div>
            ) : byUserQuery.isError ? (
              <div className="py-6 text-sm text-destructive" data-testid="text-apify-by-user-error">
                Failed to load usage by user. Please try again.
              </div>
            ) : !byUserQuery.data || byUserQuery.data.users.length === 0 ? (
              <div className="py-6 text-sm text-muted-foreground text-center" data-testid="text-apify-by-user-empty">
                No Apify runs in the selected window.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Account type</TableHead>
                      <TableHead className="text-right">Runs</TableHead>
                      <TableHead className="text-right">Buy Box city scans</TableHead>
                      <TableHead className="text-right">Total cost</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {byUserQuery.data.users.map((u) => {
                      const rowKey = u.userId == null ? "unattributed" : String(u.userId);
                      const isUnattributed = u.userId == null;
                      const drillLabel = isUnattributed ? "System / unattributed" : (u.email ?? `#${u.userId}`);
                      return (
                        <TableRow
                          key={rowKey}
                          data-testid={`row-apify-by-user-${rowKey}`}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => setDrillUser({ userId: u.userId, label: drillLabel })}
                          title="View this user's individual Apify runs"
                        >
                          <TableCell className="text-xs" data-testid={`text-apify-by-user-email-${rowKey}`}>
                            {isUnattributed ? (
                              <span className="text-muted-foreground italic">System / unattributed</span>
                            ) : (
                              u.email ?? `#${u.userId}`
                            )}
                          </TableCell>
                          <TableCell className="text-xs" data-testid={`text-apify-by-user-plan-${rowKey}`}>
                            {isUnattributed ? (
                              <span className="text-muted-foreground">—</span>
                            ) : u.planLabel ? (
                              <Badge variant="secondary">{u.planLabel}</Badge>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-xs text-right" data-testid={`text-apify-by-user-runs-${rowKey}`}>
                            {formatNum(u.runs)}
                          </TableCell>
                          <TableCell className="text-xs text-right" data-testid={`text-apify-by-user-scans-${rowKey}`}>
                            {formatNum(u.cityScans)}
                          </TableCell>
                          <TableCell className="text-right font-medium" data-testid={`text-apify-by-user-cost-${rowKey}`}>
                            {formatUsd(u.costUsd)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </Card>

          <Card data-testid="card-apify-cost-alerts">
            <div className="p-4 border-b flex flex-wrap items-center justify-between gap-3">
              <div>
                <h4 className="font-semibold">
                  Recent spike alerts
                  {apifyAlertsQuery.data && (
                    <span className="ml-2 text-xs text-muted-foreground" data-testid="text-apify-alert-counts">
                      {apifyAlertsQuery.data.unacknowledgedCount} unacknowledged · {apifyAlertsQuery.data.totalCount} total
                    </span>
                  )}
                </h4>
                <p className="text-xs text-muted-foreground">
                  Last 25 daily Apify cost-spike alerts emailed to admins from the <code>apify_cost_alerts</code> log. Rows with actor "All actors (aggregate)" reflect the daily aggregate across every Apify actor.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <Switch
                    id="switch-apify-show-acknowledged"
                    checked={showAcknowledgedApify}
                    onCheckedChange={setShowAcknowledgedApify}
                    data-testid="switch-apify-show-acknowledged"
                  />
                  <Label htmlFor="switch-apify-show-acknowledged" className="text-xs">
                    Show acknowledged
                  </Label>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => apifyAlertsQuery.refetch()}
                  data-testid="button-refresh-apify-alerts"
                >
                  Refresh
                </Button>
              </div>
            </div>
            {apifyAlertsQuery.isLoading ? (
              <div className="p-4 space-y-2">
                {[1, 2, 3].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
              </div>
            ) : apifyAlertsQuery.isError ? (
              <div className="p-6 text-sm text-destructive" data-testid="text-apify-alerts-error">
                Failed to load spike alerts.
              </div>
            ) : !apifyAlertsQuery.data || apifyAlertsQuery.data.alerts.length === 0 ? (
              <div className="p-6 text-sm text-muted-foreground" data-testid="text-apify-alerts-empty">
                {showAcknowledgedApify
                  ? "No spike alerts have been recorded."
                  : "No unacknowledged alerts. Toggle \"Show acknowledged\" to view past alerts."}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Sent</TableHead>
                      <TableHead>Alert date</TableHead>
                      <TableHead>Actor</TableHead>
                      <TableHead className="text-right">Daily cost</TableHead>
                      <TableHead className="text-right">Baseline</TableHead>
                      <TableHead className="text-right">Growth</TableHead>
                      <TableHead>Recipients</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {apifyAlertsQuery.data.alerts.map((row) => {
                      const recipientList = row.recipients.join(", ");
                      const recipientPreview = row.recipients.length <= 2
                        ? recipientList
                        : `${row.recipients.slice(0, 2).join(", ")} +${row.recipients.length - 2}`;
                      const isAck = !!row.acknowledgedAt;
                      const isPending = apifyAcknowledgeMutation.isPending && apifyAcknowledgeMutation.variables?.id === row.id;
                      const actorLabel = row.actor ?? "All actors (aggregate)";
                      return (
                        <TableRow
                          key={row.id}
                          data-testid={`row-apify-alert-${row.id}`}
                          className={isAck ? "opacity-60" : ""}
                        >
                          <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                            {formatDate(row.createdAt)}
                          </TableCell>
                          <TableCell className="text-xs whitespace-nowrap" data-testid={`text-apify-alert-date-${row.id}`}>
                            {row.alertDate}
                          </TableCell>
                          <TableCell className="text-xs font-mono" data-testid={`text-apify-alert-actor-${row.id}`}>
                            {actorLabel}
                          </TableCell>
                          <TableCell className={`text-right font-medium ${isAck ? "text-muted-foreground" : ""}`} data-testid={`text-apify-alert-cost-${row.id}`}>
                            {formatUsd(row.dailyCostUsd)}
                          </TableCell>
                          <TableCell className="text-right text-xs text-muted-foreground">
                            {formatUsd(row.baselineCostUsd)}
                          </TableCell>
                          <TableCell className="text-right">
                            <Badge variant={isAck ? "secondary" : "destructive"} data-testid={`badge-apify-alert-growth-${row.id}`}>
                              {row.growthRatio < 0 ? "∞" : `${row.growthRatio.toFixed(2)}×`}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground" title={recipientList}>
                            {recipientPreview || <span className="italic">—</span>}
                          </TableCell>
                          <TableCell className="text-xs">
                            {isAck ? (
                              <div className="text-muted-foreground" data-testid={`text-apify-alert-ack-${row.id}`}>
                                <div>Acknowledged {formatDate(row.acknowledgedAt!)}</div>
                                {row.acknowledgedBy && <div className="text-[11px]">by {row.acknowledgedBy}</div>}
                              </div>
                            ) : (
                              <Badge variant="outline">New</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {isAck ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={isPending}
                                onClick={() => apifyAcknowledgeMutation.mutate({ id: row.id, acknowledge: false })}
                                data-testid={`button-apify-alert-unacknowledge-${row.id}`}
                              >
                                Reopen
                              </Button>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={isPending}
                                onClick={() => apifyAcknowledgeMutation.mutate({ id: row.id, acknowledge: true })}
                                data-testid={`button-apify-alert-acknowledge-${row.id}`}
                              >
                                {isPending ? "…" : "Acknowledge"}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </Card>
        </>
      )}
      <ApifyAlertSettingsCard />

      <Dialog open={drillUser != null} onOpenChange={(open) => { if (!open) setDrillUser(null); }}>
        <DialogContent className="max-w-4xl" data-testid="dialog-apify-user-runs">
          <DialogHeader>
            <DialogTitle data-testid="text-apify-user-runs-title">
              Apify runs — {drillUser?.label ?? ""}
            </DialogTitle>
            <DialogDescription>
              Individual Apify runs attributed to this user over the last {byUserDays} days, most recent first.
            </DialogDescription>
          </DialogHeader>
          {drillQuery.isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : drillQuery.isError ? (
            <div className="py-6 text-sm text-destructive" data-testid="text-apify-user-runs-error">
              Failed to load this user's runs. Please try again.
            </div>
          ) : !drillQuery.data || drillQuery.data.runs.length === 0 ? (
            <div className="py-6 text-sm text-muted-foreground text-center" data-testid="text-apify-user-runs-empty">
              No Apify runs for this user in the selected window.
            </div>
          ) : (
            <div className="overflow-x-auto max-h-[60vh]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>Actor</TableHead>
                    <TableHead>Trigger</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Compute</TableHead>
                    <TableHead className="text-right">Cost</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {drillQuery.data.runs.map((r) => (
                    <TableRow key={r.id} data-testid={`row-apify-user-run-${r.id}`}>
                      <TableCell className="whitespace-nowrap text-xs">
                        {new Date(r.createdAt).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-xs font-mono">{r.actor}</TableCell>
                      <TableCell className="text-xs">
                        <div>{r.trigger}</div>
                        {r.triggerDetail && (
                          <div className="text-muted-foreground truncate max-w-[240px]">{r.triggerDetail}</div>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">{r.status}</TableCell>
                      <TableCell className="text-xs text-right">
                        {r.computeUnits == null ? "—" : r.computeUnits.toFixed(3)}
                      </TableCell>
                      <TableCell className="text-xs text-right">
                        {r.costUsd == null ? "—" : `$${r.costUsd.toFixed(4)}`}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
